import React, { useState, useCallback, useRef, useId } from "react";
import Cropper from "react-easy-crop";
import styled from "styled-components";
import { getCroppedImg, CroppedAreaPixels, dataURLtoFile } from "./helpers";
import Modal from "../Modal/modal";
import { Button, Typography } from "@atoms";
import { BrowseContainer, Cryonized, PreviewWrapper } from "components/organisms/addPeople/addPeople.styles";
import ZoomMinusIcon from "@assets/icons/zoomMinus.svg";
import ZoomPlusIcon from "@assets/icons/zoomPlus.svg";
import BrowseWhiteIcon from "@assets/icons/browse-white.svg";
import BrowseIcon from "@assets/icons/browse.svg";
import SnowIcon from "@assets/icons/snow.svg";
import AddIcon from "@assets/icons/add.svg";
import { useTranslation } from "react-i18next";
import { useStoryFormContext } from "contexts/StoryFormContext";
import { theme } from "@theme";

const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
`;

const CropperWrapper = styled.div`
  position: relative;
  aspect-ratio: 1;
  width: 440px;
  
  @media screen and (max-width: ${theme.breakpoints.sm}){
    width: 100%;
  }
`;

const SliderContainer = styled.div`
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const SliderWrapper = styled.div`
  width: 50%;
  display: flex;
  align-items: center;

  & > img {
    width: 24px;
    height: 24px;
  }

  & > button {
    width: 36px;
  }

  @media screen and (max-width: ${theme.breakpoints.sm}) {
    width: 80%;
  }
`;

const SliderInput = styled.input`
  flex: 1;
  margin: 0 10px;
  -webkit-appearance: none;
  width: 100%;
  background: transparent;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #fff;
    border: 3px solid rgb(189, 152, 127);
    border-radius: 50%;
    box-shadow: 0 0 5px rgba(189, 152, 127, 0.5);
    cursor: pointer;
    margin-top: -7px;
  }

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 6px;
    cursor: pointer;
    background: #ddd;
    border-radius: 3px;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #fff;
    border: 2px solid #000;
    border-radius: 50%;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
  &::-moz-range-track {
    width: 100%;
    height: 6px;
    cursor: pointer;
    background: #ddd;
    border-radius: 3px;
  }
`;

const IconButton = styled.button`
  line-height: 12px;
  font-size: 12px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
`;

function generateUUIDCrypto(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] & 0xf;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function ImageCropModal(): JSX.Element {
  const { t } = useTranslation();
  const { formData, updateFormData } = useStoryFormContext();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(formData.image as string || undefined);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleContainerClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback(
    (croppedArea: unknown, croppedAreaPixels: CroppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const cropImage = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const croppedDataUrl = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
        const croppedFile = dataURLtoFile(croppedDataUrl, `${generateUUIDCrypto()}.png`);
        setPreviewUrl(croppedDataUrl);
        setIsModalOpen(false);
        updateFormData({ image: croppedFile });
      } catch (error) {
        console.error(error);
      }
    }
  };


  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
        style={{ display: "none" }}
      />

      {!previewUrl ? (
        <BrowseContainer onClick={handleContainerClick}>
          <img src={BrowseIcon} alt="browse" />
          <Button icon={<img src={AddIcon} alt="add" />} style={{ width: "auto" }}>
            {t("download")}
          </Button>
          <Typography style={{ color: "#89796E", background: "none" }}>
            {t("dragAndDropFile")}
          </Typography>
          {formData.is_cryonized && (
            <Cryonized>
              <img src={SnowIcon} alt="snow" />
              {t("cryonized")}
            </Cryonized>
          )}
        </BrowseContainer>
      ) : (
        <PreviewWrapper onClick={handleContainerClick}>
          <img src={previewUrl} alt="preview" />
          <div className="preview-hover">
            <img src={BrowseWhiteIcon} alt="browse" />
            <Typography style={{ color: "#fff", background: "none" }}>
              {t("changeImage")}
            </Typography>
          </div>
          {formData.is_cryonized && (
            <Cryonized>
              <img src={SnowIcon} alt="snow" />
              {t("cryonized")}
            </Cryonized>
          )}
        </PreviewWrapper>
      )}

      <Modal fitContent isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContent>
          {imageSrc && (
            <CropperWrapper>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={322 / 440}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                // onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            </CropperWrapper>
          )}

          <SliderContainer>
            <SliderWrapper>
              <IconButton onClick={() => setZoom((prev) => Math.max(1, prev - 0.1))}>
                <img src={ZoomMinusIcon} alt="Zoom Out" />
              </IconButton>
              <SliderInput
                id="zoom-slider"
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
              />
              <IconButton onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}>
                <img src={ZoomPlusIcon} alt="Zoom In" />
              </IconButton>
            </SliderWrapper>

            {/*<SliderWrapper>*/}
            {/*  <IconButton onClick={() => setRotation((prev) => (prev - 5 + 360) % 360)}>*/}
            {/*    <img style={{ transform: 'rotate(90deg) scaleX(-1)' }} src={RotateIcon} alt="Rotate Left" />*/}
            {/*  </IconButton>*/}
            {/*  <SliderInput*/}
            {/*    id="rotation-slider"*/}
            {/*    type="range"*/}
            {/*    min="0"*/}
            {/*    max="360"*/}
            {/*    step="0.1"*/}
            {/*    value={rotation}*/}
            {/*    onChange={(e) => setRotation(parseFloat(e.target.value))}*/}
            {/*  />*/}
            {/*  <IconButton onClick={() => setRotation((prev) => (prev + 5) % 360)}>*/}
            {/*    <img style={{ transform: 'rotate(-90deg)' }} src={RotateIcon} alt="Rotate Right" />*/}
            {/*  </IconButton>*/}
            {/*</SliderWrapper>*/}
          </SliderContainer>

          <ButtonsWrapper>
            <Button variant="outlined" onClick={() => setIsModalOpen(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={cropImage}>{t("done")}</Button>
          </ButtonsWrapper>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default ImageCropModal;
